import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, Loader2, Users, TrendingUp, Calendar, 
  DollarSign, Clock, ArrowRight, Zap
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function AutoSalesOnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [progress, setProgress] = useState(20);

  useEffect(() => {
    initializeModule();
  }, []);

  const initializeModule = async () => {
    try {
      await axios.post(
        `${API}/onboarding/autosales/initialize`,
        {
          business_type: "sales",
          primary_email_tool: "gmail",
          comfort_level: "beginner"
        },
        { withCredentials: true }
      );
      setProgress(20);
    } catch (error) {
      console.error('Error initializing AutoSales:', error);
      toast.error("Failed to initialize onboarding");
    }
  };

  const handleAnalyzeActivities = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/autosales/analyze-activities`,
        { gmail_access: true },
        { withCredentials: true }
      );
      setAnalysisResults(response.data);
      setProgress(60);
      setCurrentStep(2);
      toast.success("Activity analysis complete!");
    } catch (error) {
      console.error('Error analyzing activities:', error);
      toast.error("Failed to analyze activities");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAutomation = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/autosales/automation/create-template?automation_type=lead_enrichment`,
        {},
        { withCredentials: true }
      );
      
      setProgress(100);
      setCurrentStep(3);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      toast.success("First automation created!");
    } catch (error) {
      console.error('Error creating automation:', error);
      toast.error("Failed to create automation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">AutoSales Setup Progress</span>
            <span className="text-sm text-muted-foreground">{progress}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-24">
        {currentStep === 1 && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-4xl font-heading font-semibold text-foreground mb-4">
                Welcome to AutoSales
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                AI-powered automation for sales teams. Save 15-20 hours per week with intelligent lead management.
              </p>
            </div>

            <Card className="glass-card border-primary/30">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">Save Time</h3>
                    <p className="text-sm text-muted-foreground">15-20 hours saved per week</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-blue-500" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">Close More Deals</h3>
                    <p className="text-sm text-muted-foreground">30% faster pipeline velocity</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                      <Zap className="w-5 h-5 text-purple-500" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">Quick Setup</h3>
                    <p className="text-sm text-muted-foreground">Under 10 minutes to start</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button
                onClick={handleAnalyzeActivities}
                disabled={loading}
                className="rounded-full px-8 h-12 text-lg bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing...
                  </>
                ) : (
                  <>
                    Analyze My Sales Activities <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {currentStep === 2 && analysisResults && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-3xl font-heading font-semibold text-foreground mb-4">
                Here's What We Found
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-heading font-semibold text-primary mb-2">
                    {analysisResults.patterns_found.new_leads.count}
                  </div>
                  <p className="text-sm text-muted-foreground">New Leads</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-heading font-semibold text-primary mb-2">
                    {analysisResults.patterns_found.follow_ups_needed.count}
                  </div>
                  <p className="text-sm text-muted-foreground">Follow-ups Needed</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-heading font-semibold text-primary mb-2">
                    {analysisResults.patterns_found.meeting_requests.count}
                  </div>
                  <p className="text-sm text-muted-foreground">Meeting Requests</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-heading font-semibold text-primary mb-2">
                    {analysisResults.patterns_found.proposal_tracking.count}
                  </div>
                  <p className="text-sm text-muted-foreground">Active Proposals</p>
                </CardContent>
              </Card>
            </div>

            <Card className="glass-card border-primary/50">
              <CardHeader>
                <CardTitle className="font-heading">Top Recommended Automation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-xl bg-muted/30">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-semibold text-foreground mb-1">Lead Enrichment</h3>
                      <p className="text-sm text-muted-foreground">
                        Automatically enrich new leads with company data and contact info
                      </p>
                    </div>
                    <Badge variant="outline">8h/week saved</Badge>
                  </div>
                  <Progress value={93} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">93% confidence</p>
                </div>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button
                onClick={handleCreateAutomation}
                disabled={loading}
                className="rounded-full px-8 h-12 text-lg bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...
                  </>
                ) : (
                  <>
                    Activate Lead Enrichment <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-8 max-w-3xl mx-auto text-center">
            <div>
              <div className="w-20 h-20 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-4xl font-heading font-semibold text-foreground mb-4">
                🎉 Your First Automation is Live!
              </h2>
              <p className="text-lg text-muted-foreground">
                Lead enrichment is now active and working in the background
              </p>
            </div>

            <Card className="glass-card border-green-500/30">
              <CardContent className="p-8">
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <div className="text-4xl font-heading font-semibold text-green-500 mb-2">8 hours</div>
                    <p className="text-sm text-muted-foreground">Saved per week</p>
                  </div>
                  <div className="w-px h-16 bg-border" />
                  <div className="text-center">
                    <div className="text-4xl font-heading font-semibold text-green-500 mb-2">$400</div>
                    <p className="text-sm text-muted-foreground">Value per week</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-auto py-4"
                onClick={() => navigate('/autosales/library')}
              >
                <Zap className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Add More Automations</div>
                  <div className="text-sm text-muted-foreground">Explore 4 more templates</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full h-auto py-4"
                onClick={() => navigate('/dashboard')}
              >
                <TrendingUp className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Go to Dashboard</div>
                  <div className="text-sm text-muted-foreground">See your impact metrics</div>
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
